import type { AtlasFrame } from '../ui/doll-preview.js';
import { ASSETS_BASE_URL, fetchManifest } from './manifest-loader.js';

/**
 * The packed atlas, loaded outside Phaser.
 *
 * The character selection screen draws paper dolls before the game boots, so
 * it needs the frames without a running scene. Same files, same logical keys
 * (CLAUDE.md rule 7): the manifest says which atlas holds the characters.
 *
 * Each atlas is fetched and parsed once per page and shared: the roster, the
 * profile, the party frames, the friends, the guild and the event standings
 * all ask for the bodies, most of them in the instant the world opens, and
 * the Warrior's frames alone are thousands of entries to parse.
 */

interface PackedFrame {
  frame: { x: number; y: number; w: number; h: number };
}

export interface DomAtlas {
  /** Logical body key (`body.warrior`) to its frame prefix in the atlas. */
  prefixOf: (bodyKey: string) => string | null;
  frame: (name: string) => AtlasFrame | null;
}

/** An atlas ready for CSS: its image URL, its size, and where each frame sits. */
export interface CssAtlas {
  url: string;
  width: number;
  height: number;
  rect: (name: string) => { x: number; y: number; width: number; height: number } | null;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => {
      resolve(image);
    });
    image.addEventListener('error', () => {
      reject(new Error(`Cannot load ${url}`));
    });
    image.src = url;
  });
}

interface PackedAtlas {
  image: HTMLImageElement;
  frames: Record<string, PackedFrame>;
}

const packedAtlases = new Map<string, Promise<PackedAtlas>>();

/** One atlas's image and frames, downloaded and parsed once; a failure is forgotten. */
function packedAtlas(imageFile: string, dataFile: string): Promise<PackedAtlas> {
  const known = packedAtlases.get(imageFile);
  if (known !== undefined) return known;
  const loading = Promise.all([
    loadImage(ASSETS_BASE_URL + imageFile),
    fetch(ASSETS_BASE_URL + dataFile).then((response) => {
      if (!response.ok) throw new Error(`${dataFile}: HTTP ${String(response.status)}`);
      return response.json() as Promise<unknown>;
    }),
  ]).then(([image, data]) => ({
    image,
    frames: (data as { frames?: Record<string, PackedFrame> }).frames ?? {},
  }));
  loading.catch(() => {
    packedAtlases.delete(imageFile);
  });
  packedAtlases.set(imageFile, loading);
  return loading;
}

/**
 * Load the atlas a logical sprite key lives in, for CSS backgrounds.
 * Item icons are drawn by the DOM, not by Phaser, so the panel needs the
 * packed rectangle rather than a texture.
 */
export async function loadSpriteAtlas(sample: string): Promise<CssAtlas | null> {
  try {
    const manifest = await fetchManifest();
    const atlasKey = manifest.sprites[sample]?.atlas;
    const atlas = atlasKey === undefined ? undefined : manifest.atlases[atlasKey];
    if (atlas === undefined) return null;
    const { image, frames } = await packedAtlas(atlas.image, atlas.data);
    return {
      url: ASSETS_BASE_URL + atlas.image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      rect: (name) => {
        const packed = frames[name];
        if (packed === undefined) return null;
        return {
          x: packed.frame.x,
          y: packed.frame.y,
          width: packed.frame.w,
          height: packed.frame.h,
        };
      },
    };
  } catch {
    return null;
  }
}

let characterAtlasOnce: Promise<DomAtlas | null> | undefined;

/**
 * Load the atlases that hold the playable bodies (`characters-<class>`, one
 * per class with its rig's overlays). Returns null if anything is missing,
 * and the next caller then tries again.
 */
export function loadCharacterAtlas(): Promise<DomAtlas | null> {
  characterAtlasOnce ??= readCharacterAtlas().then((atlas) => {
    if (atlas === null) characterAtlasOnce = undefined;
    return atlas;
  });
  return characterAtlasOnce;
}

async function readCharacterAtlas(): Promise<DomAtlas | null> {
  try {
    const manifest = await fetchManifest();
    const atlasKeys = [
      ...new Set(
        Object.entries(manifest.sheets)
          .filter(([key, sheet]) => key.startsWith('body.') && sheet.atlas.startsWith('characters'))
          .map(([, sheet]) => sheet.atlas),
      ),
    ];
    const loaded = await Promise.all(
      atlasKeys.map((atlasKey) => {
        const atlas = manifest.atlases[atlasKey];
        if (atlas === undefined) throw new Error(`No atlas ${atlasKey}`);
        return packedAtlas(atlas.image, atlas.data);
      }),
    );
    if (loaded.length === 0) return null;
    return {
      prefixOf: (bodyKey) => manifest.sheets[bodyKey]?.prefix ?? null,
      frame: (name) => {
        for (const { image, frames } of loaded) {
          const packed = frames[name];
          if (packed === undefined) continue;
          return {
            image,
            x: packed.frame.x,
            y: packed.frame.y,
            width: packed.frame.w,
            height: packed.frame.h,
          };
        }
        return null;
      },
    };
  } catch {
    // The selection screen degrades to names and classes without previews.
    return null;
  }
}
